import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Message {
  id: string;
  user_id: string;
  from_user_id?: string;
  from_system: boolean;
  subject: string;
  snippet?: string;
  body?: string;
  read_at?: string;
  archived_at?: string;
  created_at: string;
}

export function useMessages() {
  const queryClient = useQueryClient();

  const { data: messages = [], ...query } = useQuery({
    queryKey: ['messages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .is('archived_at', null)
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      return data as Message[];
    },
    staleTime: 30_000,
    retry: 0,
  });

  const unreadCount = messages.filter(m => !m.read_at).length;

  const markAsRead = useMutation({
    mutationFn: async (messageIds: string[]) => {
      const { error } = await supabase
        .from('messages')
        .update({ read_at: new Date().toISOString() })
        .in('id', messageIds);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  const archiveMessage = useMutation({
    mutationFn: async (messageId: string) => {
      const { error } = await supabase
        .from('messages')
        .update({ archived_at: new Date().toISOString() })
        .eq('id', messageId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('messages-realtime')
      .on('postgres_changes', 
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['messages'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return {
    data: messages,
    unreadCount,
    markAsRead: markAsRead.mutate,
    archiveMessage: archiveMessage.mutate,
    isMarkingRead: markAsRead.isPending,
    ...query,
  };
}